import {isEnumValue} from '@Utils/enum.util';
import {SocialPlatform} from '@Components/social-media/social-media.types';
import {getComparableSocialPlatform} from '@Libraries/social-media-platform.library';
import {
  addConnectAccountBroadcastChannel,
  getAccountConnectionResponseFromBroadcast,
  handleAccountConnectionResponse,
  removeConnectAccountBroadcastChannel,
} from '@Libraries/social-media-account.library';
import type {ComponentActionDoneHandler, ComponentActionTarget} from './component-action.types';

interface StartedSocialAccountTarget {
  target: ComponentActionTarget;
  platform: SocialPlatform;
}

interface SocialAccountConnectionListener {
  recordStart: (target: ComponentActionTarget, platform: SocialPlatform) => void;
  markConnected: (target: ComponentActionTarget) => void;
  close: () => void;
}

export const getTargetPlatform = (target: ComponentActionTarget): SocialPlatform | null => {
  const platform = target.actionData?.platform;

  return isEnumValue(SocialPlatform, platform) ? platform : null;
};

/** A widget the user never clicked ignores broadcasts, so older widgets in the chat stay silent. */
export const listenForSocialAccountConnections = (onDone: ComponentActionDoneHandler): SocialAccountConnectionListener => {
  const startedTargets: StartedSocialAccountTarget[] = [];
  let isOpen = true;

  const markConnected = (target: ComponentActionTarget): void => {
    if (isOpen) {
      onDone(target);
    }
  };

  const channel = addConnectAccountBroadcastChannel((event: MessageEvent): void => {
    if (startedTargets.length === 0) {
      return;
    }

    const connectionResponse = getAccountConnectionResponseFromBroadcast(event);
    handleAccountConnectionResponse(connectionResponse, (): void => {
      const connectedTarget = findConnectedTarget(startedTargets, connectionResponse?.socialMediaPlatform);
      if (connectedTarget !== null) {
        markConnected(connectedTarget);
      }
    });
  });

  return {
    recordStart: (target: ComponentActionTarget, platform: SocialPlatform): void => {
      startedTargets.push({target, platform});
    },
    markConnected,
    close: (): void => {
      isOpen = false;
      removeConnectAccountBroadcastChannel(channel);
    },
  };
};

const findConnectedTarget = (startedTargets: StartedSocialAccountTarget[], connectedPlatform: SocialPlatform | undefined): ComponentActionTarget | null => {
  if (connectedPlatform === undefined) {
    return startedTargets[startedTargets.length - 1].target;
  }

  const comparablePlatform = getComparableSocialPlatform(connectedPlatform);
  const platformMatch = [...startedTargets].reverse().find((startedTarget) => getComparableSocialPlatform(startedTarget.platform) === comparablePlatform);

  return platformMatch?.target ?? null;
};
